import {
  Box,
  Card,
  Flex,
  FormatNumber,
  Heading,
  Spinner,
  Stack,
  Table,
  Text,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";

import { makeRequest } from "@/network/api";

type BattleGroupStats = {
  totalBattleGroups: number;
  smallestBattleGroup: number;
  largestBattleGroup: number;
  totalUsersRemaining: number;
  totalTokensRemaining: number;
  unlockStats: Array<{
    rank: number;
    totalTokens: number;
    medianScore: number;
    maxScore: number;
    minScore: number;
  }>;
};

type ApiResponse = {
  stats: BattleGroupStats;
  plumePrice: number;
};

export const BattleGroupReview = () => {
  const [data, setData] = useState<ApiResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBattleGroupStats = async () => {
      try {
        setIsLoading(true);
        const response = await makeRequest("/admin/battlegroups/stats");
        const responseData = await response.json();
        setData(responseData.data);
      } catch (error) {
        console.error("Error fetching battle group stats:", error);
        setError("Failed to load battle group statistics");
      } finally {
        setIsLoading(false);
      }
    };

    fetchBattleGroupStats();
  }, []);

  const formatTokenValue = (tokens: number, plumePrice?: number) => {
    if (!plumePrice) return tokens.toLocaleString();

    const tokenValueUsd = tokens * plumePrice;
    return `${tokens.toLocaleString()} (~$${tokenValueUsd.toLocaleString(undefined, { maximumFractionDigits: 0 })})`;
  };

  if (isLoading) {
    return (
      <Flex justifyContent="center" alignItems="center" height="50vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Box p={4} maxW="1200px" mx="auto">
        <Heading mb={6}>Battle Group Review</Heading>
        <Card.Root p={4}>
          <Card.Body>
            <Text color="red.500">{error}</Text>
          </Card.Body>
        </Card.Root>
      </Box>
    );
  }

  const stats = data?.stats;
  const plumePrice = data?.plumePrice || 0;

  return (
    <Box p={0} maxW="1200px" mx="auto">
      <Heading mb={6}>Battle Group Review</Heading>

      {stats && (
        <>
          <Card.Root p={4} mb={6}>
            <Card.Body>
              <Flex justify="space-between" wrap="wrap" gap={4}>
                <Box>
                  <Text fontSize="sm" fontWeight="medium" color="gray.500">
                    Total Battle Groups
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold">
                    {stats.totalBattleGroups}
                  </Text>
                </Box>

                <Box>
                  <Text fontSize="sm" fontWeight="medium" color="gray.500">
                    Battle Group Size
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold">
                    {stats.smallestBattleGroup} - {stats.largestBattleGroup}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    users per group
                  </Text>
                </Box>

                <Box>
                  <Text fontSize="sm" fontWeight="medium" color="gray.500">
                    Total Users Remaining
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold">
                    {stats.totalUsersRemaining.toLocaleString()}
                  </Text>
                </Box>

                <Box>
                  <Text fontSize="sm" fontWeight="medium" color="gray.500">
                    Total Tokens Remaining
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold">
                    {formatTokenValue(stats.totalTokensRemaining, plumePrice)}
                  </Text>
                </Box>
              </Flex>
            </Card.Body>
          </Card.Root>

          <Card.Root p={4}>
            <Card.Header>
              <Heading size="md">
                Number of tokens that unlock based on # of winning users
              </Heading>
            </Card.Header>
            <Card.Body>
              <Stack gap={4}>
                {stats.unlockStats.length > 0 ? (
                  <Table.Root>
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeader>Top X Users</Table.ColumnHeader>
                        <Table.ColumnHeader>Total Tokens</Table.ColumnHeader>
                        <Table.ColumnHeader>Token Value</Table.ColumnHeader>
                        <Table.ColumnHeader>Median Score</Table.ColumnHeader>
                        <Table.ColumnHeader>Max Score</Table.ColumnHeader>
                        <Table.ColumnHeader>Min Score</Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {stats.unlockStats.map((stat) => (
                        <Table.Row
                          key={stat.rank}
                          fontWeight={stat.rank === 10 ? "bold" : "normal"}
                        >
                          <Table.Cell>{stat.rank}</Table.Cell>
                          <Table.Cell>
                            {stat.totalTokens.toLocaleString()}
                          </Table.Cell>
                          <Table.Cell>
                            <FormatNumber
                              value={
                                plumePrice ? stat.totalTokens * plumePrice : 0
                              }
                              style="currency"
                              currency="USD"
                            />
                          </Table.Cell>
                          <Table.Cell>
                            {stat.medianScore.toLocaleString()}
                          </Table.Cell>
                          <Table.Cell>
                            {stat.maxScore.toLocaleString()}
                          </Table.Cell>
                          <Table.Cell>
                            {stat.minScore.toLocaleString()}
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                ) : (
                  <Text color="gray.500">No unlock statistics available</Text>
                )}
              </Stack>
            </Card.Body>
          </Card.Root>
        </>
      )}
    </Box>
  );
};
